import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROLE_DASHBOARD_ROUTE } from '../../modules/auth/constants';

const RESPONSIVE_CSS = `
@media (max-width: 768px) {
  .hcms-login-left { display: none !important; }
  .hcms-login-right { flex: 1 1 100% !important; padding: 24px !important; }
}
.hcms-login-input:focus { border-color: #3B82F6 !important; box-shadow: 0 0 0 3px rgba(59,130,246,0.1) !important; }
.hcms-login-btn:hover { background: #334155 !important; }
.hcms-login-btn:disabled { opacity: 0.6; cursor: not-allowed; }
`;

/**
 * HCMS Login Page — authenticates against /api/v2/auth/login
 * and redirects to the role-appropriate dashboard.
 */
function HcmsLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, isLoading, isAuthenticated } = useAuth();

  // If already logged in, redirect to the role-based dashboard.
  useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      const target = ROLE_DASHBOARD_ROUTE[user.role] || '/hcms/dashboard';
      if (location.pathname !== target) {
        navigate(target, { replace: true });
      }
    }
  }, [isLoading, isAuthenticated, user, navigate, location.pathname]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password) {
      setError('Email and password are required.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/v2/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password })
      });

      const data = await res.json();

      if (!data.success) {
        setError(data.message || 'Login failed. Please try again.');
        return;
      }

      login(data.token, data.user);

      // If the user was redirected to login from a protected page, send them back.
      if (from) {
        navigate(from, { replace: true });
      }
      // Otherwise, the useEffect above will redirect to the role-based dashboard.
    } catch (err) {
      console.error('[HcmsLogin] error:', err);
      setError('Network error. Please check that the server is running.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <style>{RESPONSIVE_CSS}</style>
      {/* Left branding panel */}
      <div className="hcms-login-left" style={styles.leftPanel}>
        <div style={styles.leftOverlay} />
        <div style={styles.leftContent}>
          <div style={styles.logoWrap}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <span style={styles.logoText}>HCMS</span>
          </div>
          <h1 style={styles.leftTitle}>Case Management<br />System</h1>
          <p style={styles.leftSubtitle}>
            Streamline your HR case management with intelligent routing,<br />
            SLA tracking, and seamless collaboration.
          </p>
          <div style={styles.featureList}>
            <div style={styles.featureItem}>
              <div style={styles.featureDot} />
              <span>Smart ticket routing & escalation</span>
            </div>
            <div style={styles.featureItem}>
              <div style={styles.featureDot} />
              <span>Real-time SLA monitoring</span>
            </div>
            <div style={styles.featureItem}>
              <div style={styles.featureDot} />
              <span>Role-based dashboards & analytics</span>
            </div>
          </div>
        </div>
        <div style={styles.leftFooter}>© {new Date().getFullYear()} HCMS. All rights reserved.</div>
      </div>

      {/* Right sign-in panel */}
      <div className="hcms-login-right" style={styles.rightPanel}>
        <div style={styles.card}>
          <h2 style={styles.title}>Welcome Back</h2>
          <p style={styles.subtitle}>Sign in to your account to continue</p>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                style={styles.input}
                className="hcms-login-input"
                required
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="hcms-login-input"
                style={styles.input}
                required
              />
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <button type="submit" disabled={loading} className="hcms-login-btn" style={styles.button}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
  },
  leftPanel: {
    flex: '1 1 55%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '60px 64px',
    background: 'linear-gradient(135deg, #1E293B 0%, #334155 40%, #475569 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  leftOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(96,165,250,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(96,165,250,0.06) 0%, transparent 50%)',
    zIndex: 1,
  },
  leftContent: {
    position: 'relative',
    zIndex: 2,
    maxWidth: 480,
  },
  logoWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 48,
  },
  logoText: {
    fontSize: 22,
    fontWeight: 800,
    color: '#FFFFFF',
    letterSpacing: '0.05em',
  },
  leftTitle: {
    fontSize: 36,
    fontWeight: 800,
    color: '#FFFFFF',
    lineHeight: 1.25,
    margin: '0 0 20px',
  },
  leftSubtitle: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.7)',
    lineHeight: 1.6,
    margin: '0 0 40px',
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    color: 'rgba(255,255,255,0.85)',
    fontSize: 14,
    fontWeight: 500,
  },
  featureDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#60A5FA',
    flexShrink: 0,
  },
  leftFooter: {
    position: 'absolute',
    bottom: 32,
    left: 64,
    fontSize: 12,
    color: 'rgba(255,255,255,0.4)',
    zIndex: 2,
  },
  rightPanel: {
    flex: '1 1 45%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#F8FAFC',
    padding: 40,
  },
  card: {
    width: 400,
    maxWidth: '100%',
    padding: 0,
  },
  title: {
    margin: '0 0 8px',
    fontSize: 26,
    fontWeight: 800,
    color: '#1E293B',
  },
  subtitle: {
    margin: '0 0 32px',
    color: '#64748B',
    fontSize: 14,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
  },
  input: {
    padding: '12px 14px',
    fontSize: 14,
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  error: {
    padding: '12px 14px',
    background: '#FEF2F2',
    color: '#DC2626',
    borderRadius: 8,
    fontSize: 13,
    border: '1px solid #FECACA',
  },
  button: {
    padding: '13px 16px',
    fontSize: 15,
    fontWeight: 600,
    color: '#FFFFFF',
    background: '#1E293B',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
};

export default HcmsLogin;
